import { WEARABLES, WEARABLE_ORDER, type WearableId, type WearableSlot } from '../economy/wardrobe';
import { RARITY_LABEL } from '../economy/rarity';

export interface WardrobeCallbacks {
  isOwned: (id: WearableId) => boolean;
  getEquipped: (slot: WearableSlot) => WearableId | null;
  /** `id: null` unequips the slot — same "tap the equipped one again to remove it" convention this screen's own body wires up. */
  onEquip: (slot: WearableSlot, id: WearableId | null) => void;
  onClose: () => void;
}

/** Exported alongside `buildWearableCards` — `ui/stash.ts`'s [STREET FITS] section iterates the same 3 slots with the same labels. */
export const SLOT_LABEL: Record<WearableSlot, string> = { head: 'HEAD', torso: 'TORSO', back: 'BACK' };
export const SLOTS: readonly WearableSlot[] = ['head', 'torso', 'back'];

/**
 * Wave I (PLAN-CITY3 §5, I-3: "[STREET FITS]... share the wardrobe's card
 * builder") — the one row-of-cards builder both this screen and
 * `ui/stash.ts`'s STASH summary now call, extracted verbatim from what used
 * to be `WardrobeScreen.refresh()`'s own inline loop body (byte-identical
 * markup/behavior, not a redesign) so the two screens can never quietly
 * drift into two different "what does an owned/locked wearable card look
 * like" answers.
 */
export function buildWearableCards(slot: WearableSlot, isOwned: (id: WearableId) => boolean, equipped: WearableId | null, onEquip: (id: WearableId | null) => void): HTMLDivElement {
  const cards = document.createElement('div');
  cards.className = 'br3d-wardrobe-cards';
  for (const id of WEARABLE_ORDER) {
    const def = WEARABLES[id];
    if (def.slot !== slot) continue;
    const owned = isOwned(id);
    const card = document.createElement('button');
    card.className = `br3d-wearcard br3d-rarity-${def.rarity}`;
    card.classList.toggle('br3d-wearcard-equipped', equipped === id);
    card.classList.toggle('br3d-wearcard-locked', !owned);
    if (owned) {
      const name = document.createElement('div');
      name.className = 'br3d-wearcard-name';
      name.textContent = def.label;
      const rarity = document.createElement('div');
      rarity.className = 'br3d-wearcard-rarity';
      rarity.textContent = RARITY_LABEL[def.rarity];
      card.append(name, rarity);
      card.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        e.preventDefault();
        onEquip(equipped === id ? null : id);
      });
    } else {
      card.textContent = '???';
      card.disabled = true;
    }
    cards.append(card);
  }
  return cards;
}

/**
 * Wave L-2 (PLAN-CITY2 §6): the bodega wardrobe stand — same full-screen
 * overlay idiom as `ui/huntboard.ts` (reuses `ui/pause.ts`'s own
 * `.br3d-pausescreen-title`/`.br3d-menu-btn` classes directly, same as that
 * screen does), driven by main.ts's single `uiState` (this screen owns the
 * `'wardrobe'` value). One row per slot; each OWNED piece for that slot is a
 * tappable card (tapping the already-equipped one unequips it — a second
 * tap toggles off, no separate "unequip" button needed); an un-owned piece
 * renders as a locked "???" card so its EXISTENCE is visible (worth hunting
 * for) without spoiling which monster drops it (Karan's own "how did you get
 * that" flex is a discovery, not a shopping list). Rebuilds from the CURRENT
 * owned/equipped state every time it's shown (`refresh()`), same "never
 * cache a stale list from a prior visit" discipline `HuntBoard.refresh()`
 * already established.
 */
export class WardrobeScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLDivElement;

  constructor(private readonly callbacks: WardrobeCallbacks) {
    this.root = document.createElement('div');
    this.root.className = 'br3d-pausescreen br3d-wardrobe';
    this.root.style.display = 'none';

    const title = document.createElement('div');
    title.className = 'br3d-pausescreen-title';
    title.textContent = 'WARDROBE';

    this.body = document.createElement('div');
    this.body.className = 'br3d-wardrobe-body';

    this.root.append(title, this.body, this.menuButton('BACK', () => this.callbacks.onClose()));
    document.body.append(this.root);
  }

  private menuButton(label: string, onPress: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = 'br3d-menu-btn';
    btn.textContent = label;
    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      onPress();
    });
    return btn;
  }

  private refresh(): void {
    this.body.replaceChildren();
    for (const slot of SLOTS) {
      const row = document.createElement('div');
      row.className = 'br3d-wardrobe-row';
      const label = document.createElement('div');
      label.className = 'br3d-wardrobe-slot-label';
      label.textContent = SLOT_LABEL[slot];
      row.append(label);

      const equipped = this.callbacks.getEquipped(slot);
      const cards = buildWearableCards(slot, this.callbacks.isOwned, equipped, (id) => this.callbacks.onEquip(slot, id));
      row.append(cards);
      this.body.append(row);
    }
  }

  /** Called by main.ts's single `setUiState()`, alongside `pauseMenu.setVisible()`/`huntBoard.setVisible()` — see those functions' own comments. */
  setVisible(visible: boolean): void {
    if (visible) this.refresh();
    this.root.style.display = visible ? 'flex' : 'none';
  }
}
